import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileSignature, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatCurrency, formatDate, formatDateOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { FileUpload } from "@/components/FileUpload";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type Status = "ACTIVE" | "EXPIRED" | "PENDING_RENEWAL" | "TERMINATED";

interface Contract {
  id: string;
  vendorName: string;
  serviceType: string;
  startDate: string;
  endDate: string;
  renewalDate?: string | null;
  amount: string;
  status: Status;
  fileUrl?: string | null;
  notes?: string | null;
}

const statusVariant: Record<Status, "success" | "default" | "warning" | "danger"> = {
  ACTIVE: "success",
  EXPIRED: "default",
  PENDING_RENEWAL: "warning",
  TERMINATED: "danger",
};

function daysUntil(date: string): number {
  return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function renewalColor(days: number): { label: string; bg: string; text: string } {
  if (days < 0) return { label: "Vencido", bg: "bg-slate-100", text: "text-slate-500" };
  if (days < 30) return { label: `${days}d`, bg: "bg-red-50", text: "text-red-700" };
  if (days < 60) return { label: `${days}d`, bg: "bg-amber-50", text: "text-amber-700" };
  if (days < 90) return { label: `${days}d`, bg: "bg-yellow-50", text: "text-yellow-700" };
  return { label: `${days}d`, bg: "bg-green-50", text: "text-green-700" };
}

export function ContractsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editContract, setEditContract] = useState<Contract | null>(null);

  const { data: contracts = [], isLoading, isError } = useQuery({
    queryKey: ["contracts"],
    queryFn: async () => (await api.get<Contract[]>("/contracts")).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/contracts/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contracts"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSignature size={24} /> {t("legal.contracts")}
        </h1>
        {user?.role === "ADMIN" && (
          <button onClick={() => setOpen(true)} className="btn-primary">
            <Plus size={16} /> {t("legal.newContract")}
          </button>
        )}
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : contracts.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("legal.vendor")}</th>
                <th className="text-left px-4 py-2">{t("legal.serviceType")}</th>
                <th className="text-left px-4 py-2">{t("legal.endDate")}</th>
                <th className="text-right px-4 py-2">{t("legal.amount")}</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-center px-4 py-2">{t("legal.daysLeft")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contracts.map((c) => {
                const days = daysUntil(c.endDate);
                const color = renewalColor(days);
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">
                      {c.vendorName}
                      {c.notes && <div className="text-xs text-slate-500">{c.notes}</div>}
                    </td>
                    <td className="px-4 py-2">{c.serviceType}</td>
                    <td className="px-4 py-2">{formatDateOnly(c.endDate, i18n.language)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(c.amount)}</td>
                    <td className="px-4 py-2"><Badge variant={statusVariant[c.status]}>{t(`legal.contractStatus.${c.status}`)}</Badge></td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${color.bg} ${color.text}`}>{color.label}</span>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-3">
                        {c.fileUrl && (
                          <a href={c.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1">
                            <ExternalLink size={12} /> PDF
                          </a>
                        )}
                        <AdminRecordActions
                          compact
                          onEdit={() => setEditContract(c)}
                          onDelete={() => deleteMutation.mutate(c.id)}
                          entityLabel="el contrato"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Leyenda: <span className="bg-red-50 text-red-700 px-1 rounded">&lt;30d</span> ·{" "}
        <span className="bg-amber-50 text-amber-700 px-1 rounded">30-60d</span> ·{" "}
        <span className="bg-yellow-50 text-yellow-700 px-1 rounded">60-90d</span> ·{" "}
        <span className="bg-green-50 text-green-700 px-1 rounded">&gt;90d</span>
      </p>

      <NewContractModal open={open} onClose={() => setOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["contracts"] })} />
      {editContract && (
        <NewContractModal
          key={editContract.id}
          open
          contract={editContract}
          onClose={() => setEditContract(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["contracts"] })}
        />
      )}
    </div>
  );
}

function NewContractModal({ open, contract, onClose, onDone }: { open: boolean; contract?: Contract | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const isEdit = !!contract;
  const [form, setForm] = useState({
    vendorName: contract?.vendorName ?? "",
    serviceType: contract?.serviceType ?? "",
    startDate: contract ? contract.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    endDate: contract ? contract.endDate.slice(0, 10) : "",
    renewalDate: contract?.renewalDate ? contract.renewalDate.slice(0, 10) : "",
    amount: contract ? String(contract.amount) : "",
    fileUrl: contract?.fileUrl ?? "",
    notes: contract?.notes ?? "",
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        amount: parseFloat(form.amount),
        renewalDate: form.renewalDate ? new Date(form.renewalDate).toISOString() : null,
        fileUrl: form.fileUrl || null,
        notes: form.notes || null,
      };
      if (isEdit && contract) return (await api.put(`/contracts/${contract.id}`, body)).data;
      return (await api.post("/contracts", body)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
      if (!isEdit) setForm({ vendorName: "", serviceType: "", startDate: new Date().toISOString().slice(0, 10), endDate: "", renewalDate: "", amount: "", fileUrl: "", notes: "" });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("common.edit") : t("legal.newContract")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("legal.vendor")}</label>
            <input className="input" required value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.serviceType")}</label>
            <input className="input" required value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">{t("legal.startDate")}</label>
            <input type="date" className="input" required value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.endDate")}</label>
            <input type="date" className="input" required value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("legal.renewalDate")}</label>
            <input type="date" className="input" value={form.renewalDate} onChange={(e) => setForm({ ...form, renewalDate: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">{t("legal.amount")} (USD)</label>
          <input type="number" step="0.01" className="input" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div>
          <label className="label">Notas</label>
          <textarea className="input min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <FileUpload label="PDF del contrato" folder="costadorada1/contracts" value={form.fileUrl} onUploaded={(url) => setForm({ ...form, fileUrl: url })} />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
